// shared-lib/millwork/casing.js
/**
 * Door, window and cased-opening casing for ONE room: each side of an
 * opening that faces this room, as the pieces a carpenter cuts (spec §2).
 * An interior door's other side belongs to the room it faces: a side counts
 * for a room when the point just off that face lies on the room's own floor.
 * So a door between two rooms is counted once per side, and never twice, as
 * long as the two rooms' floors do not overlap there — where they do, both
 * rooms count the side.
 *
 * Pure.
 */
const { CASING_WIDTH_M } = require('./defaults');
const { sideProbePoints, roomHoldsPoint } = require('./openingSides');
const { trimColourHex, openingTrimHex } = require('./colours');
const { isDemoedOpening } = require('../openings/openingSync');
const { isScanned } = require('../scanned-label');

const SIDES = ['A', 'B'];
// Opening.jsx: `revealColorFor = (style, color) => style && style !== 'none'
// ? trimHexFor(color) : wallColor`, and `trimHexFor = (id) => TRIM_HEX[id] ||
// TRIM_HEX.white` (openingCasing.js) — a styled side with no colour is white.
const OPENING_DEFAULT_COLOUR = '#FFFFFF';
// A window's Side B sill the renderer draws when none is set (WindowWithTrim).
const RENDERED_SIDE_B_SILL_M = 0.15;

// Each default below is the renderer's, undefined-only (a destructuring
// default): an explicit null stays null.
const dflt = (v, d) => (v === undefined ? d : v);

// Door.jsx: `trimStyleSideA = doorData.trimStyleSideA || doorData.trimStyle
// || 'simple'`. WindowWithTrim.create: `trimStyleSideA = trimStyle` with
// `trimStyle = 'simple'`. Opening: a side is cased only when it has a style.
const styleOf = (kind, cat, side) => {
  const own = cat[`trimStyleSide${side}`];
  if (kind === 'door') return own || cat.trimStyle || 'simple';
  if (kind === 'window') return dflt(own, dflt(cat.trimStyle, 'simple'));
  return own || null;
};

// Door.jsx: `trimColorSideA = doorData.trimColorSideA || '#ffffff'` (no
// legacy trimColor). WindowWithTrim.create: `trimColorSideA = trimColor`,
// `trimColor = 0xffffff`, drawn through parseColor(.., 0xffffff).
// Opening.jsx / openingCasing.js: `trimHexFor(color)` (TRIM_HEX table).
const colourOf = (kind, cat, side) => {
  const own = cat[`trimColorSide${side}`];
  if (kind === 'door') return trimColourHex(own || '#ffffff');
  if (kind === 'window') {
    return (
      trimColourHex(dflt(own, dflt(cat.trimColor, 0xffffff))) ??
      OPENING_DEFAULT_COLOUR
    );
  }
  return openingTrimHex(own).toUpperCase();
};

// WindowWithTrim.create: `trimWidthSideA = trimWidth`, `trimWidth = 0.1`.
const widthOf = (kind, cat, side) =>
  kind === 'window'
    ? dflt(cat[`trimWidthSide${side}`], dflt(cat.trimWidth, CASING_WIDTH_M))
    : CASING_WIDTH_M;

// WindowWithTrim.create: `sillOnSideA = sillDepthSideA != null &&
// sillColorSideA != null` (both undefined by default); Side B defaults
// `sillDepthSideB = legacySillDepth ?? 0.15`, `sillColorSideB =
// legacySillColor ?? 0xe8e8e8`, so only an explicit null turns it off.
const hasStool = (cat, side) => {
  if (side === 'A')
    return cat.sillDepthSideA != null && cat.sillColorSideA != null;
  const depth = dflt(
    cat.sillDepthSideB,
    cat.sillDepth ?? RENDERED_SIDE_B_SILL_M
  );
  const colour = dflt(cat.sillColorSideB, cat.sillColor ?? 0xe8e8e8);
  return depth != null && colour != null;
};

const piecesFor = (kind, cat, side, W, h, w) => {
  if (kind !== 'window') {
    return [
      { role: 'leg', lenM: h + w },
      { role: 'leg', lenM: h + w },
      { role: 'head', lenM: W + 2 * w },
    ];
  }
  if (hasStool(cat, side)) {
    return [
      { role: 'leg', lenM: h + w },
      { role: 'leg', lenM: h + w },
      { role: 'head', lenM: W + 2 * w },
      { role: 'stool', lenM: W + 2 * w },
    ];
  }
  return [
    { role: 'leg', lenM: h + 2 * w },
    { role: 'leg', lenM: h + 2 * w },
    { role: 'head', lenM: W + 2 * w },
    { role: 'bottom', lenM: W + 2 * w },
  ];
};

const parentWallOf = (room, opening) => {
  const pid = opening?.parentWallIds?.[0];
  if (!pid) return null;
  return (
    (room.walls || []).find(
      (w) => w.id === pid || w.alignment?.structureWallId === pid
    ) || null
  );
};

/**
 * @param {Object} room
 * @param {{doors?, windows?, openings?}} openings  HYDRATED
 * (No `rooms` argument: a side is read from this room's own floor alone.)
 */
const casingForRoom = (
  room,
  { doors = [], windows = [], openings = [] } = {}
) => {
  const out = [];
  const lists = [
    ['door', doors],
    ['window', windows],
    ['opening', openings],
  ];
  for (const [kind, list] of lists) {
    for (const o of list || []) {
      if (isDemoedOpening(o)) continue;
      const W = o?.dimensions?.x;
      const h = o?.dimensions?.y;
      if (!(W > 0) || !(h > 0)) continue;
      const cat = o.category?.[kind] || {};
      // A side counts for this room when the probe point off that face is
      // inside THIS room's own floor — no other room is consulted, so the
      // casing never depends on which rooms share the list (scoping a take-off
      // to fewer rooms must equal building it without them). By the room
      // itself: a room keyed by id may carry no `id`.
      const probes = sideProbePoints(o, {
        wallThicknessM: parentWallOf(room, o)?.dimensions?.z || 0,
      });
      if (!probes) continue;
      for (const side of SIDES) {
        if (!roomHoldsPoint(room, probes[side])) continue;
        const style = styleOf(kind, cat, side);
        if (!style || style === 'none') continue;
        const w = widthOf(kind, cat, side);
        out.push({
          openingId: o.structureOpeningId ?? o.id,
          kind,
          side,
          style,
          colour: colourOf(kind, cat, side),
          widthM: w,
          scanned: isScanned(o),
          pieces: piecesFor(kind, cat, side, W, h, w),
        });
      }
    }
  }
  return out;
};

module.exports = { casingForRoom };
